import React from 'react';
import clsx from 'clsx';

import styles from './TabSwitcher.module.scss';

export interface TabOption {
  value: string;
  label: string;
  ariaControls?: string;
  ariaDescribedBy?: string;
  testId?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
}

export interface TabSwitcherProps {
  activeTab: string;
  tabs: TabOption[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  ariaLabelledBy?: string;
  labelColor?: string;
  activeLabelColor?: string;
  className?: string;
  style?: React.CSSProperties;
}

const TabSwitcher: React.FC<TabSwitcherProps> = ({
  activeTab,
  tabs,
  onChange,
  name = 'tabSwitcher',
  ariaLabelledBy,
  className,
  labelColor,
  activeLabelColor,
  style,
}) => {
  const activeIndex = tabs.findIndex((tab) => tab.value === activeTab);

  return (
    <div
      className={clsx(styles.tabSwitcherWrapper, className)}
      data-testid='TabSwitcher'
      role='tablist'
      aria-labelledby={ariaLabelledBy}
      style={{ '--tab-count': tabs.length, '--active-index': activeIndex, ...style } as React.CSSProperties}
    >
      <div className={clsx(styles.slider)} />
      {tabs.map((tab) => (
        <div className={clsx(styles.tabOption, tab.className)} key={tab.value}>
          <div className={styles.tabLabel}>
            {tab.icon && tab.iconPosition === 'left' && tab.icon}
            <label
              htmlFor={`Option${tab.value}`}
              style={{ color: activeTab === tab.value ? activeLabelColor : labelColor }}
            >
              {tab.label}
            </label>
            {tab.icon && tab.iconPosition === 'right' && tab.icon}
          </div>
          <input
            type='radio'
            id={`Option${tab.value}`}
            value={tab.value}
            name={name}
            onChange={onChange}
            checked={activeTab === tab.value}
            data-testid={tab.testId || `Tab${tab.value}Input`}
            role='tab'
            aria-controls={tab.ariaControls}
            aria-selected={activeTab === tab.value}
            aria-describedby={tab.ariaDescribedBy}
          />
        </div>
      ))}
    </div>
  );
};

export default TabSwitcher;
